import React from 'react';
import { Content, Row, Col, Title, Text, Pagination, Image } from 'components';
import { Button, Empty, Flex } from 'antd';
import { encrypt } from 'utils/Crypto';
import dayjs from 'dayjs';

const TAP = [
  { label: '전체', value: 'ALL' },
  { label: '진행중', value: 'ING' },
  { label: '진행예정', value: 'PREV' },
  { label: '종료', value: 'END' },
];

const PAGE_COUNT = 12;
const PAGE_SIZE = 12;

const PromotionHomePresenter = ({
  navigate,
  data = [],
  screenSize,
  type = 'PROMOTION',
}) => {
  const [activeButton, setActiveButton] = React.useState('ALL');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [promotionList, setPromotionList] = React.useState([...data]);

  /* ===== Hooks ===== */
  /**
   * 타입 키
   * --
   */
  const typeKey = React.useMemo(() => {
    return type === 'RECIPE' ? `group_product` : 'promotion';
  }, [type]);

  /**
   * 빈값 텍스트
   * --
   */
  const emptyMessage = React.useMemo(() => {
    return type === 'RECIPE'
      ? '준비된 레시피가 없습니다.'
      : '진행 중인 프로모션이 없습니다.';
  }, [type]);

  /**
   * 탭키 필터
   * --
   */
  React.useEffect(() => {
    if (!data.length) return setPromotionList([]);

    const filtered = data.filter((item) =>
      activeButton === 'ALL' ? true : item.status === activeButton
    );

    setPromotionList(filtered);
  }, [activeButton, data]);

  /* ===== Styles ===== */
  const styles = {
    title: {
      fontSize: screenSize >= 768 ? 64 : screenSize >= 500 ? 40 : 28,
      fontFamily: `"Dela Gothic One", sans-serif`,
      fontWeight: 400,
      fontStyle: 'normal',
      textTransform: 'capitalize',
    },
    button: {
      padding: '10px 29px',
      backgroundColor: 'transparent',
      border: '1px solid #ddd',
      color: 'var(--black-color)',
    },
    activeButton: {
      padding: '10px 29px',
      backgroundColor: 'var(--black-color)',
      color: 'white',
    },
    item: {
      width: '100%',
      boxSizing: 'border-box',
      cursor: 'pointer',
      padding: screenSize >= 768 ? '0 20px' : 0,
    },
    image: {
      width: '100%',
      height: 'auto',
      aspectRatio: '5 / 2.93',
      maxWidth: screenSize >= 768 ? '443px' : '100%',
    },
    titleText: {
      fontSize: screenSize >= 768 ? 24 : screenSize >= 500 ? 20 : 16,
      fontWeight: 700,
      marginTop: 12,
      cursor: 'pointer',
    },
    desc: {
      fontSize: screenSize >= 768 ? 18 : 16,
      color: 'var(--dark-gray-color)',
      cursor: 'pointer',
    },
    date: {
      fontSize: screenSize >= 768 ? 14 : 12,
      color: 'var(--light-gray-color)',
      cursor: 'pointer',
    },
    emptyState: {
      textAlign: 'center',
      padding: 20,
      fontSize: 16,
      color: 'gray',
      width: '100%',
    },
    tapStyle: {
      width: screenSize >= 768 ? 100 : screenSize >= 500 ? 80 : 60,
      height: screenSize >= 768 ? 48 : screenSize >= 500 ? 40 : 35,
      borderRadius: 100,
      fontSize: screenSize >= 768 ? 16 : screenSize >= 500 ? 14 : 13,
    },
  };

  return (
    <Content maxWidth={1820} padding={screenSize >= 768 ? '120px 0' : '80px 0'}>
      <Row gutter={25}>
        <Col x={0} md={1} lg={2} />
        <Col x={24} md={22} lg={20}>
          <Content maxWidth="100%" padding={0}>
            <Title
              align="center"
              style={{
                ...styles.title,
                marginBottom: screenSize >= 768 ? 120 : 60,
              }}
            >
              {type === 'RECIPE' ? 'Recipe' : 'Promotion'}
            </Title>

            {/* 필터 탭 */}
            {type === 'PROMOTION' ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: screenSize >= 768 ? 12 : 7,
                  marginBottom: screenSize >= 768 ? 100 : 60,
                }}
              >
                {TAP.map((tab) => (
                  <Button
                    key={`tab-${tab.value}`}
                    style={{
                      ...styles.tapStyle,
                      ...(activeButton === tab.value
                        ? styles.activeButton
                        : styles.button),
                    }}
                    onClick={() => setActiveButton(tab.value)}
                  >
                    {tab.label}
                  </Button>
                ))}
              </div>
            ) : null}

            {/* 리스트 */}
            <Row gutter={screenSize >= 500 ? [80, 40] : [40, 20]}>
              {promotionList.length ? (
                promotionList
                  .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
                  .map((item) => (
                    <Col
                      key={`item-${item[`${typeKey}_id`]}`}
                      x={24}
                      md={12}
                      lg={8}
                      style={styles.item}
                      onClick={() =>
                        navigate(
                          `${type.toLocaleLowerCase()}/${encodeURIComponent(
                            encrypt(item[`${typeKey}_id`])
                          )}`
                        )
                      }
                    >
                      <Image
                        src={item.src}
                        alt={item[`${typeKey}_title`]}
                        style={styles.image}
                      />
                      <Text style={styles.titleText}>
                        {item[`${typeKey}_title`]}
                      </Text>
                      <Text style={styles.desc}>
                        {item[`${typeKey}_description`]}
                      </Text>
                      {type === 'PROMOTION' ? (
                        <Flex align="center" gap={4}>
                          <Text style={styles.date}>
                            {dayjs
                              .unix(item[`${typeKey}_start_date`])
                              .format('YYYY.MM.DD')}
                          </Text>
                          <Text style={styles.date}>~</Text>
                          <Text style={styles.date}>
                            {dayjs
                              .unix(item[`${typeKey}_end_date`])
                              .format('YYYY.MM.DD')}
                          </Text>
                        </Flex>
                      ) : null}
                    </Col>
                  ))
              ) : (
                <Col x={24} style={styles.emptyState}>
                  <Empty description={emptyMessage} />
                </Col>
              )}
            </Row>

            {/* 페이지네이션 */}
            <div style={{ marginTop: 100 }}>
              <Pagination
                total={promotionList.length}
                pageSize={PAGE_COUNT}
                current={currentPage}
                onChange={(e) => setCurrentPage(e)}
                showSizeChanger
                onShowSizeChange={(current, size) => {
                  setCurrentPage(1);
                }}
                showPage={5}
                buttonStyle={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#999999',
                  background: 'none',
                  width: 28,
                  height: 28,
                }}
                isButton={true}
                activeStyle={{
                  color: `var(--black-color) !important`,
                  borderBottom: '2px solid #111111',
                }}
                hoverStyle={{
                  color: `var(--black-color) !important`,
                }}
                navStyle={{
                  border: '1px solid var(--black-color)',
                }}
                isLatestButton={true}
              />
            </div>
          </Content>
        </Col>
        <Col x={0} md={1} lg={2} />
      </Row>
    </Content>
  );
};

export default PromotionHomePresenter;
